import { IDataArrayDictionary } from '~/components/LineGraph/entity/types';
import { IPositionDetails } from '~/pages/Headcount/entity/types';
import { calculateExpenseAmount } from '~/pages/Expenses/utils/calculateExpenseAmount';
import { IStringDate } from '~/utils/stringDate/types';
import * as stringDate from '~/utils/stringDate';
import type { IFormula } from '~/services/parallel/formulas.types';
import { ExpenseFrequencyEnum } from '../types';

export const graphDataByType = ({
  expenseFormulas,
  startDate,
  endDate,
}: {
  expenseFormulas: IFormula[];
  startDate: IStringDate;
  endDate: IStringDate;
}): {
  typeData: IDataArrayDictionary[];
  categoryData: IDataArrayDictionary[];
  totalData: IDataArrayDictionary[];
} => {
  const typeData: IDataArrayDictionary[] = [];
  const categoryData: IDataArrayDictionary[] = [];
  const totalData: IDataArrayDictionary[] = [];

  const monthArray = stringDate.createMonthArrayBetweenDates({
    startDate,
    endDate,
  });
  monthArray.forEach((month) => {
    const time = new Date(month).getTime();
    const monthTypeData: Record<string, number> = {
      date: time,
      oneTime: 0,
      monthly: 0,
      quarterly: 0,
      annual: 0,
      onHire: 0,
    };
    const monthCategoryData: Record<string, number> = {
      date: time,
      software: 0,
      other: 0,
      peopleAndFacilities: 0,
      cogs: 0,
      marketing: 0,
    };
    const monthTotalData: Record<string, number> = {
      date: time,
      total: 0,
    };

    expenseFormulas.forEach((expenseFormula) => {
      const expenseContext = expenseFormula.context?.expenseContext;
      if (!expenseContext) {
        return;
      }

      monthTotalData.total +=
        expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))?.value ??
        0;

      switch (expenseContext.tag) {
        case 'Software':
          monthCategoryData.software +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case 'Other':
          monthCategoryData.other +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case 'People & Facilities':
          monthCategoryData.peopleAndFacilities +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case 'Cost of Goods Sold':
          monthCategoryData.cogs +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case 'Marketing':
          monthCategoryData.marketing +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        default:
          break;
      }

      switch (expenseContext.frequency) {
        case ExpenseFrequencyEnum.OneTime:
          monthTypeData.oneTime +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case ExpenseFrequencyEnum.Monthly:
          monthTypeData.monthly +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case ExpenseFrequencyEnum.Quarterly:
          monthTypeData.quarterly +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case ExpenseFrequencyEnum.Annually:
          monthTypeData.annual +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        case ExpenseFrequencyEnum.OnHire:
          monthTypeData.onHire +=
            expenseFormula.calculations.find((calc) => stringDate.isSameMonth({ date1: month, date2: calc.date }))
              ?.value ?? 0;
          break;
        default:
          break;
      }
    });

    typeData.push(monthTypeData);
    categoryData.push(monthCategoryData);
    totalData.push(monthTotalData);
  });

  return { typeData, categoryData, totalData };
};
